import React, { useEffect, useState } from 'react';
import styled, { keyframes, createGlobalStyle, css } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart, type CartItem } from '@context/CartContext';
import { useAudio } from '@context/AudioContext';
import ImageWithFallback from '@components/common/ImageWithFallback';

// The inquiry is delivered as a prefilled email: the site has no server-side
// form endpoint (the old form silently faked success), so mailto: is the only
// delivery that always works. Keep this address in sync with the Contact page.
const INQUIRY_EMAIL = 'contact@blagmanbros.com';

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const ripple = keyframes`
  0% { transform: scale(0.95); opacity: 1; }
  100% { transform: scale(1.3); opacity: 0; }
`;

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(78, 205, 196, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(78, 205, 196, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(78, 205, 196, 0);
  }
`;

// Global animations
const CartAnimations = createGlobalStyle`
  .pulsing {
    animation: ${pulse} 2s infinite;
  }
`;

// Styled Components
const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(3px);
  z-index: ${({ theme }) => theme.zIndex.overlay};
`;

const SidebarContainer = styled(motion.div)`
  position: fixed;
  top: 0;
  right: 0;
  width: 440px;
  height: 100%;
  background: ${({ theme }) => theme.colors.surface};
  border-left: 3px solid ${({ theme }) => theme.colors.primary};
  box-shadow: -8px 0 0 ${({ theme }) => theme.colors.overlay.medium};
  z-index: ${({ theme }) => theme.zIndex.modal};
  display: flex;
  flex-direction: column;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 380px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    width: 100%;
    border-left: none;
  }
`;

const SidebarHeader = styled.div`
  padding: ${({ theme }) => theme.spacing['5']};
  border-bottom: 1px solid ${({ theme }) => theme.colors.overlay.light};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SidebarTitle = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontSizes['2xl']};
  font-family: ${({ theme }) => theme.fonts.subheading};

  span {
    display: block;
    font-family: ${({ theme }) => theme.fonts.ui};
    font-size: ${({ theme }) => theme.fontSizes.xs};
    font-weight: 600;
    letter-spacing: 0.28em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.accent1};
    margin-bottom: ${({ theme }) => theme.spacing['1']};
  }
`;

const CloseButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.overlay.light};
  color: ${({ theme }) => theme.colors.text};
  border: none;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.quick};
  
  &:hover {
    background: ${({ theme }) => theme.colors.primary};
    transform: rotate(90deg);
  }
  
  svg {
    width: 24px;
    height: 24px;
    fill: currentColor;
  }
`;

const SidebarContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing['5']};
`;

const EmptyCart = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing['8']} 0;
  color: ${({ theme }) => theme.colors.textMuted};
  
  h3 {
    margin-bottom: ${({ theme }) => theme.spacing['4']};
    font-family: ${({ theme }) => theme.fonts.heading};
    color: ${({ theme }) => theme.colors.textMuted};
  }
  
  p {
    margin-bottom: ${({ theme }) => theme.spacing['6']};
  }
  
  svg {
    width: 80px;
    height: 80px;
    fill: ${({ theme }) => theme.colors.textMuted};
    margin-bottom: ${({ theme }) => theme.spacing['4']};
  }
`;

const CartList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const CartItem = styled(motion.li)`
  display: flex;
  gap: ${({ theme }) => theme.spacing['4']};
  padding: ${({ theme }) => theme.spacing['3']} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.overlay.light};
  
  &:last-child {
    border-bottom: none;
  }
`;

const RippleEffect = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primary}40;
  animation: ${ripple} 1.5s ease-out infinite;
`;

const AlbumCover = styled.div<{ isPlaying?: boolean }>`
  width: 60px;
  height: 60px;
  border-radius: ${({ isPlaying }) => isPlaying ? '50%' : '10px'};
  overflow: hidden;
  position: relative;
  transition: all 0.3s ease;
  
  ${({ isPlaying }) => isPlaying && css`
    animation: ${rotate} 20s linear infinite;
  `}
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 15px;
    height: 15px;
    background-color: ${({ theme }) => theme.colors.surfaceAlt};
    border-radius: 50%;
    opacity: ${({ isPlaying }) => isPlaying ? 1 : 0};
    z-index: 2;
    transition: opacity 0.3s ease;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(
      circle at center,
      transparent 30%,
      rgba(0, 0, 0, 0.5) 100%
    );
    opacity: ${({ isPlaying }) => isPlaying ? 1 : 0};
    z-index: 1;
    transition: opacity 0.3s ease;
  }
`;

const DefaultCover = styled.div<{ isPlaying?: boolean }>`
  width: 60px;
  height: 60px;
  border-radius: ${({ isPlaying }) => isPlaying ? '50%' : '10px'};
  background: ${({ theme }) => theme.colors.overlay.light};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  transition: all 0.3s ease;
  
  ${({ isPlaying }) => isPlaying && css`
    animation: ${rotate} 20s linear infinite;
  `}
  
  svg {
    width: 30px;
    height: 30px;
    fill: ${({ theme }) => theme.colors.textMuted};
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 15px;
    height: 15px;
    background: ${({ theme }) => theme.colors.surfaceAlt};
    border-radius: 50%;
    opacity: ${({ isPlaying }) => isPlaying ? 1 : 0};
    z-index: 2;
    transition: opacity 0.3s ease;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(
      circle at center,
      transparent 30%,
      rgba(0, 0, 0, 0.5) 100%
    );
    opacity: ${({ isPlaying }) => isPlaying ? 1 : 0};
    z-index: 1;
    transition: opacity 0.3s ease;
  }
`;

const ItemInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ItemTitle = styled.h3`
  margin: 0 0 ${({ theme }) => theme.spacing['1']};
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ theme }) => theme.colors.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ItemArtist = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.primary};
`;

const ItemControls = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing['2']};
  align-items: center;
`;

const ItemButton = styled.button<{ active?: boolean }>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme, active }) => active ? theme.colors.primary : theme.colors.overlay.light};
  color: ${({ theme }) => theme.colors.text};
  border: none;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.quick};
  
  &:hover {
    transform: scale(1.1);
    background: ${({ theme, active }) => active ? theme.colors.accent1 : theme.colors.primary};
  }
  
  svg {
    width: 18px;
    height: 18px;
    fill: currentColor;
  }
  
  /* Animation applied conditionally via className */
`;

const SidebarFooter = styled.div`
  padding: ${({ theme }) => theme.spacing['5']};
  border-top: 2px solid ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.surfaceAlt};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing['3']};
  max-height: 60%;
  overflow-y: auto;
`;

const InquiryForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing['3']};
`;

const FormRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing['2']};

  > * {
    flex: 1;
    min-width: 0;
  }
`;

const FieldLabel = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.textMuted};
  margin-bottom: ${({ theme }) => theme.spacing['1']};
`;

const fieldStyles = css`
  width: 100%;
  padding: ${({ theme }) => theme.spacing['2']} ${({ theme }) => theme.spacing['3']};
  background: ${({ theme }) => theme.colors.surface};
  border: 2px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  transition: border-color ${({ theme }) => theme.transitions.quick};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.secondary};
  }
`;

const TextField = styled.input`
  ${fieldStyles}
`;

const SelectField = styled.select`
  ${fieldStyles}
`;

const NotesField = styled.textarea`
  ${fieldStyles}
  resize: vertical;
  min-height: 64px;
`;

const SendButton = styled(motion.button)`
  padding: ${({ theme }) => theme.spacing['3']};
  background: ${({ theme }) => theme.colors.accent1};
  color: ${({ theme }) => theme.colors.surface};
  border: 2px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  box-shadow: ${({ theme }) => theme.shadows.md};
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  letter-spacing: 0.06em;
  text-transform: uppercase;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.quick};

  &:hover {
    background: ${({ theme }) => theme.colors.secondary};
    transform: translate(-1px, -1px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }
`;

const SentNote = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.success};
  line-height: 1.5;

  a {
    color: ${({ theme }) => theme.colors.accent1};
    font-weight: 600;
  }
`;

const ClearButton = styled(motion.button)`
  padding: ${({ theme }) => theme.spacing['1']};
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  border: none;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.quick};

  &:hover {
    color: ${({ theme }) => theme.colors.accent1};
  }
`;

// Animation variants
const slideIn = {
  hidden: { x: 400 },
  visible: {
    x: 0,
    transition: {
      type: "spring",
      damping: 25,
      stiffness: 250
    }
  },
  exit: {
    x: 400,
    transition: {
      type: "spring",
      damping: 25,
      stiffness: 250
    }
  }
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.3
    }
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.2
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, x: 20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      type: "spring",
      damping: 25,
      stiffness: 250
    }
  },
  exit: {
    opacity: 0,
    x: -20,
    transition: {
      duration: 0.2
    }
  }
};

// Hoisted hover/tap animations to avoid object recreation
const CHECKOUT_HOVER = { scale: 1.03 };
const CHECKOUT_TAP = { scale: 0.97 };
const CLEAR_HOVER = { scale: 1.05 };

// Component Props
interface CartSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

// License Inquiry Panel (formerly a shopping-cart sidebar — the e-commerce
// metaphor confused music supervisors, so the panel IS the inquiry form now)
const CartSidebar: React.FC<CartSidebarProps> = ({ isOpen, onClose }) => {
  const { cartItems, removeFromCart, clearCart } = useCart();
  const { play, currentTrack, isPlaying } = useAudio();

  const [inquiry, setInquiry] = useState({ name: '', email: '', projectType: '', notes: '' });
  const [sent, setSent] = useState(false);

  const handleField = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setInquiry(prev => ({ ...prev, [name]: value }));
    setSent(false);
  };

  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    const trackList = cartItems.map(item => `- "${item.title}" — ${item.artist}`).join('\n');
    const subject = `License inquiry: ${cartItems.length} track${cartItems.length === 1 ? '' : 's'} from the Blagman Brothers catalog`;
    const body =
      `Name: ${inquiry.name}\n` +
      `Email: ${inquiry.email}\n` +
      (inquiry.projectType ? `Project type: ${inquiry.projectType}\n` : '') +
      `\nTracks of interest:\n${trackList}\n` +
      (inquiry.notes ? `\nProject details:\n${inquiry.notes}\n` : '');
    window.location.href = `mailto:${INQUIRY_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setSent(true);
  };

  // Handle escape key press
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);

    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Play a track
  const playTrack = (track: CartItem) => {
    play(track);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <CartAnimations />
          <Overlay
            variants={fadeIn}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={onClose}
          />

          <SidebarContainer
            variants={slideIn}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <SidebarHeader>
              <SidebarTitle><span>License Inquiry</span>Your Selected Tracks</SidebarTitle>
              <CloseButton onClick={onClose} aria-label="Close">
                <svg viewBox="0 0 24 24">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                </svg>
              </CloseButton>
            </SidebarHeader>

            <SidebarContent>
              {cartItems.length > 0 ? (
                <CartList>
                  <AnimatePresence>
                    {cartItems.map((item) => (
                      <CartItem
                        key={item.id}
                        variants={itemVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        layout
                      >
                        {item.coverArt ? (
                          <AlbumCover isPlaying={isPlaying && currentTrack?.id === item.id}>
                            {isPlaying && currentTrack?.id === item.id && <RippleEffect />}
                            <ImageWithFallback
                              src={item.coverArt}
                              alt={item.title}
                            />
                          </AlbumCover>
                        ) : (
                          <DefaultCover isPlaying={isPlaying && currentTrack?.id === item.id}>
                            {isPlaying && currentTrack?.id === item.id && <RippleEffect />}
                            <svg viewBox="0 0 24 24">
                              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 14.5c-2.49 0-4.5-2.01-4.5-4.5S9.51 7.5 12 7.5s4.5 2.01 4.5 4.5-2.01 4.5-4.5 4.5zm0-5.5c-.55 0-1 .45-1 1s.45 1 1 1 1-.45 1-1-.45-1-1-1z" />
                            </svg>
                          </DefaultCover>
                        )}

                        <ItemInfo>
                          <ItemTitle>{item.title}</ItemTitle>
                          <ItemArtist>{item.artist}</ItemArtist>
                        </ItemInfo>

                        <ItemControls>
                          <ItemButton
                            onClick={() => playTrack(item)}
                            active={isPlaying && currentTrack?.id === item.id}
                            aria-label={isPlaying && currentTrack?.id === item.id ? "Pause" : "Play"}
                            className={isPlaying && currentTrack?.id === item.id ? "pulsing" : ""}
                          >
                            {isPlaying && currentTrack?.id === item.id ? (
                              <svg viewBox="0 0 24 24">
                                <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                              </svg>
                            ) : (
                              <svg viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z" />
                              </svg>
                            )}
                          </ItemButton>

                          <ItemButton
                            onClick={() => removeFromCart(item.id)}
                            aria-label="Remove from cart"
                          >
                            <svg viewBox="0 0 24 24">
                              <path d="M19 13H5v-2h14v2z" />
                            </svg>
                          </ItemButton>
                        </ItemControls>
                      </CartItem>
                    ))}
                  </AnimatePresence>
                </CartList>
              ) : (
                <EmptyCart>
                  <svg viewBox="0 0 24 24">
                    <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z" />
                  </svg>
                  <h3>No tracks selected yet</h3>
                  <p>
                    Browse the catalog and press <strong>License</strong> on any track —
                    it lands here, ready to send as a licensing request.
                  </p>
                </EmptyCart>
              )}
            </SidebarContent>

            {cartItems.length > 0 && (
              <SidebarFooter>
                <InquiryForm onSubmit={handleSend}>
                  <FormRow>
                    <div>
                      <FieldLabel htmlFor="inquiry-name">Name</FieldLabel>
                      <TextField
                        id="inquiry-name"
                        name="name"
                        value={inquiry.name}
                        onChange={handleField}
                        required
                        autoComplete="name"
                      />
                    </div>
                    <div>
                      <FieldLabel htmlFor="inquiry-email">Email</FieldLabel>
                      <TextField
                        id="inquiry-email"
                        name="email"
                        type="email"
                        value={inquiry.email}
                        onChange={handleField}
                        required
                        autoComplete="email"
                      />
                    </div>
                  </FormRow>

                  <div>
                    <FieldLabel htmlFor="inquiry-project">Project Type</FieldLabel>
                    <SelectField
                      id="inquiry-project"
                      name="projectType"
                      value={inquiry.projectType}
                      onChange={handleField}
                    >
                      <option value="">Select (optional)</option>
                      <option>Film &amp; Television</option>
                      <option>Commercial &amp; Advertising</option>
                      <option>Documentary</option>
                      <option>Video Games &amp; Interactive</option>
                      <option>Social Media Content</option>
                      <option>Podcast &amp; Audio</option>
                      <option>Other</option>
                    </SelectField>
                  </div>

                  <div>
                    <FieldLabel htmlFor="inquiry-notes">Project Details</FieldLabel>
                    <NotesField
                      id="inquiry-notes"
                      name="notes"
                      value={inquiry.notes}
                      onChange={handleField}
                      placeholder="Timeline, usage, territory — anything that helps us quote fast."
                    />
                  </div>

                  <SendButton
                    type="submit"
                    whileHover={CHECKOUT_HOVER}
                    whileTap={CHECKOUT_TAP}
                  >
                    Send License Request ({cartItems.length})
                  </SendButton>

                  {sent && (
                    <SentNote>
                      Your email app should now open with the request ready to send.
                      If it didn&apos;t, write us directly at{' '}
                      <a href={`mailto:${INQUIRY_EMAIL}`}>{INQUIRY_EMAIL}</a>.
                    </SentNote>
                  )}
                </InquiryForm>

                <ClearButton
                  onClick={clearCart}
                  whileHover={CLEAR_HOVER}
                >
                  Remove All Tracks
                </ClearButton>
              </SidebarFooter>
            )}
          </SidebarContainer>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartSidebar;